import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useManifest } from '../lib/data.jsx'
import { useSearchCompanies, searchAll } from '../lib/appSearch.js'
import { ResultIcon, Highlight } from '../components/searchUi.jsx'
import { useRbac } from '../lib/rbac.js'

// Full search results — same sections as the topbar preview (shared
// lib/appSearch), rendered Revailo-style: a centered, width-capped column with
// a big "Search results" heading and one bordered TABLE per section (header
// row + hairline rows), not full-width stacked cards. The topbar search is the
// input; this page just presents ?q's results.

const splitTrades = (v) =>
  String(v || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)

// Per-section column labels; sections without an entry fall back to generic.
const COLUMN_LABELS = {
  leads: ['Company', 'Location', 'Industry'],
  markets: ['Market', 'Details'],
  sequences: ['Sequence', 'Details'],
  segments: ['Segment', 'Details'],
  pages: ['Page', 'Section'],
}

function leadCells(item) {
  const c = item.state?.company || {}
  const trades = splitTrades(c.trade)
  return [
    c.city_state || '—',
    trades.length
      ? `${trades.slice(0, 2).join(', ')}${trades.length > 2 ? ` +${trades.length - 2}` : ''}`
      : '—',
  ]
}

export default function SearchResults() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const query = params.get('q') || ''
  const { manifest } = useManifest()
  const companies = useSearchCompanies(true)

  const { can } = useRbac()
  const { sections, total } = useMemo(
    () => searchAll(query, { companies, manifest, perSection: 8, can }),
    [query, companies, manifest, can],
  )

  return (
    <div className="mx-auto w-full max-w-[880px] pt-10">
      <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-ink">Search results</h1>
      <p className="m-0 mt-2 text-[14.5px] text-dim">
        {!query
          ? 'Type in the search bar above to search the workspace.'
          : `${total.toLocaleString()} result${total === 1 ? '' : 's'} for “${query}”`}
      </p>

      {query && total === 0 && (
        <div className="mt-16 text-center text-[14px] text-dim">
          {companies === null
            ? 'Searching…'
            : `No matches for “${query}”. Try a company, a market (e.g. “Florida”), or a page.`}
        </div>
      )}

      {sections.map((section) => {
        const labels = COLUMN_LABELS[section.key] || [section.label, 'Details']
        const isLeads = section.key === 'leads'
        return (
          <section key={section.key} className="mt-12 first-of-type:mt-10">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="m-0 text-[19px] font-bold tracking-[-0.01em] text-ink">
                {section.label}
              </h2>
              <span className="text-[13px] text-faint">
                {section.total.toLocaleString()} result{section.total === 1 ? '' : 's'}
              </span>
            </div>

            <div className="overflow-hidden rounded-[14px] border border-line bg-surface">
              {/* Column header */}
              <div
                className={`grid items-center gap-x-4 px-5 py-[10px] text-[12.5px] font-medium text-dim [border-bottom:1px_solid_var(--table-line)] ${
                  isLeads ? 'grid-cols-[1fr_180px_220px] max-[720px]:grid-cols-[1fr_150px]' : 'grid-cols-[1fr_220px] max-[560px]:grid-cols-[1fr]'
                }`}
              >
                {labels.map((l, i) => (
                  <span key={l} className={isLeads && i === 2 ? 'max-[720px]:hidden' : i > 0 ? 'max-[560px]:hidden' : ''}>
                    {l}
                  </span>
                ))}
              </div>

              {section.items.map((item) => {
                const extra = isLeads ? leadCells(item) : [item.subtitle || '—']
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(item.to, { state: item.state })}
                    className={`grid w-full items-center gap-x-4 px-5 py-[13px] text-left transition-colors hover:bg-panel [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)] ${
                      isLeads ? 'grid-cols-[1fr_180px_220px] max-[720px]:grid-cols-[1fr_150px]' : 'grid-cols-[1fr_220px] max-[560px]:grid-cols-[1fr]'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-x-3">
                      <span className="grid h-8 w-8 flex-none place-items-center rounded-[8px] border border-line bg-panel text-faint [&_svg]:h-4 [&_svg]:w-4">
                        <ResultIcon icon={item.icon} />
                      </span>
                      <span className="truncate text-[14px] font-semibold text-ink">
                        <Highlight text={item.title} query={query} />
                      </span>
                    </span>
                    {extra.map((cell, i) => (
                      <span
                        key={i}
                        className={`truncate text-[13.5px] text-dim ${
                          isLeads && i === 1 ? 'max-[720px]:hidden' : i > 0 || !isLeads ? 'max-[560px]:hidden' : ''
                        }`}
                      >
                        <Highlight text={cell} query={query} />
                      </span>
                    ))}
                  </button>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
